import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Paragraph,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { ArchiveSummary, WorkEnvironmentArchive } from '../../../api/workEnvironmentTypes';
import {
  useAskForSignatureMutation,
  useRegisterSignatureDeviationMutation,
  useWithdrawSignatureMutation,
} from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { signatureActions } from '../archiveView';
import { reminderStatus } from '../reminderView';
import { missingSuffix } from '../safetyRepView';
import { TextField } from '../components/FormParts';
import classes from './Evaluation.module.css';
import { TakenOverNotice } from '../components/TakenOverNotice';

type Props = {
  surveyId: number;
  signature: ArchiveSummary['signature'];
  safetyRep: Pick<WorkEnvironmentArchive, 'hasSafetyRep' | 'safetyRepMissing' | 'canRegisterDeviation' | 'takenOverBy'>;
};

const K = 'workEnvironmentArchive.checklist.signature';

/**
 * The team leader asks the safety representative to sign, or registers a deviation after they
 * returned it. Without a safety representative, the deviation is registered with that as the reason.
 */
export const SignatureActions = ({
  surveyId,
  signature,
  safetyRep,
}: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const { hasSafetyRep } = safetyRep;
  const noRep = missingSuffix(safetyRep.safetyRepMissing);
  const [writing, setWriting] = useState(false);
  const [reason, setReason] = useState(
    hasSafetyRep ? '' : t(`${K}.deviationDefault${noRep}`),
  );
  const [tried, setTried] = useState(false);
  const ask = useAskForSignatureMutation(surveyId);
  const deviation = useRegisterSignatureDeviationMutation(surveyId);
  const withdraw = useWithdrawSignatureMutation(surveyId);
  const actions = signatureActions(signature, safetyRep);
  const asked = reminderStatus(ask, t, i18n.language);

  const registerDeviation = () => {
    setTried(true);
    if (reason.trim())
      deviation.mutate(reason.trim(), { onSuccess: () => setWriting(false) });
  };

  if (writing) {
    return (
      <span className={classes.signatureForm}>
        <TextField
          id="signature-deviation"
          label={t(`${K}.deviationReason`)}
          help={t(`${K}.deviationHelp${noRep}`)}
          value={reason}
          error={
            tried && !reason.trim()
              ? t(`${K}.deviationError`)
              : undefined
          }
          onChange={setReason}
        />
        <span className={classes.signatureButtons}>
          <Button
            data-size="sm"
            variant="secondary"
            loading={deviation.isPending}
            onClick={registerDeviation}
          >
            {t(`${K}.registerDeviation`)}
          </Button>
          <Button
            data-size="sm"
            variant="tertiary"
            onClick={() => setWriting(false)}
          >
            {t(`${K}.cancel`)}
          </Button>
        </span>
      </span>
    );
  }

  return (
    <span className={classes.signatureForm}>
      {(actions.ask || actions.remind) && <TakenOverNotice emails={safetyRep.takenOverBy} />}
      {(actions.ask ||
        actions.remind ||
        actions.withdraw ||
        actions.deviation) && (
        <span className={classes.signatureButtons}>
          {(actions.ask || actions.remind) && (
            <Button
              data-size="sm"
              variant="secondary"
              loading={ask.isPending}
              onClick={() => ask.mutate(undefined)}
            >
              {t(actions.remind ? `${K}.remind` : `${K}.request`)}
            </Button>
          )}
          {actions.withdraw && (
            <Button
              data-size="sm"
              variant="tertiary"
              className={classes.inlineAction}
              loading={withdraw.isPending}
              onClick={() => withdraw.mutate(undefined)}
            >
              {t(`${K}.withdraw`)}
            </Button>
          )}
          {actions.deviation && (
            <Button
              data-size="sm"
              variant={hasSafetyRep ? 'tertiary' : 'secondary'}
              className={hasSafetyRep ? classes.inlineAction : undefined}
              onClick={() => setWriting(true)}
            >
              {t(`${K}.deviation${noRep}`)}
            </Button>
          )}
        </span>
      )}
      {asked.sent && (
        <Paragraph data-size="xs" role="status">
          {asked.sent}
        </Paragraph>
      )}
      {asked.failed && (
        <ValidationMessage data-size="sm">{asked.failed}</ValidationMessage>
      )}
      {(deviation.isError || withdraw.isError) && (
        <ValidationMessage data-size="sm">
          {t(`${K}.failed`)}
        </ValidationMessage>
      )}
    </span>
  );
};
